import { groupByApp } from '../lib/groups.js';

interface GroupPickerProps {
  /** Every group available in the pool (from GET /api/groups). */
  allGroups: { name: string; description?: string }[];
  /** Groups currently selected / assigned. */
  selected: string[];
  onToggle: (group: string) => void;
  /** Groups with an in-flight change; rendered dimmed and non-interactive. */
  pending?: string[];
  disabled?: boolean;
}

/**
 * Permission picker laid out one application per row with its roles as toggle chips.
 * Shared by the per-user editor and the create-user modal.
 */
export function GroupPicker({
  allGroups,
  selected,
  onToggle,
  pending = [],
  disabled = false,
}: GroupPickerProps) {
  const sections = groupByApp(allGroups);
  if (sections.length === 0) {
    return <div className="text-xs text-brand-400">No groups defined.</div>;
  }
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 items-center">
      {sections.map((section) => {
        const anySelected = section.groups.some((g) => selected.includes(g.name));
        return (
          <div key={section.app} className="contents">
            <div
              className={
                'text-xs font-display font-medium whitespace-nowrap ' +
                (anySelected ? 'text-brand-800' : 'text-brand-500')
              }
            >
              {section.label}
            </div>
            <div className="flex flex-wrap gap-1">
              {section.groups.map((g) => {
                const checked = selected.includes(g.name);
                const busy = pending.includes(g.name);
                return (
                  <button
                    key={g.name}
                    type="button"
                    title={g.description || g.name}
                    aria-pressed={checked}
                    disabled={disabled || busy}
                    onClick={() => onToggle(g.name)}
                    className={
                      'text-xs px-2 py-0.5 border transition-opacity disabled:cursor-wait ' +
                      (busy ? 'opacity-50 ' : '') +
                      (checked
                        ? 'bg-brand-600 text-white border-brand-600'
                        : 'bg-white text-brand-700 border-brand-200 hover:border-brand-500')
                    }
                  >
                    {g.role || g.name}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
